/**
 * Density Store — Zustand store for user density preference override.
 * Story 13.6: Persona-Adaptive Density System (AC-5, AC-7)
 *
 * Persists to localStorage immediately; syncs to server fire-and-forget.
 */

import { create } from "zustand";
import type { DensityMode } from "@/shared/context/persona-context";

const STORAGE_KEY = "ztd-density-override";

interface DensityStore {
  /** User override (null = use persona default) */
  densityOverride: DensityMode | null;
  /** Set density override and persist */
  setDensityOverride: (density: DensityMode | null) => void;
  /** Clear override (revert to persona default) */
  clearOverride: () => void;
  /** Initialize from localStorage on app boot */
  hydrate: () => void;
}

const useDensityStore = create<DensityStore>((set) => ({
  densityOverride: null,

  setDensityOverride: (density) => {
    if (density) {
      localStorage.setItem(STORAGE_KEY, density);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    set({ densityOverride: density });
    // Server sync is fire-and-forget — not blocking UI
    syncToServer(density).catch(console.error);
  },

  clearOverride: () => {
    localStorage.removeItem(STORAGE_KEY);
    set({ densityOverride: null });
    syncToServer(null).catch(console.error);
  },

  hydrate: () => {
    const stored = localStorage.getItem(STORAGE_KEY) as DensityMode | null;
    if (stored && ["compact", "default", "comfortable"].includes(stored)) {
      set({ densityOverride: stored });
    }
  },
}));

async function syncToServer(density: DensityMode | null): Promise<void> {
  try {
    await fetch("/api/v1/user/preferences", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
      body: JSON.stringify({ densityOverride: density }),
    });
  } catch {
    // Silently fail — localStorage is the source of truth for immediate UX
  }
}

export { useDensityStore, STORAGE_KEY };
